import { type ReactNode, useMemo } from 'react';
import type { ProgressSummary } from '@/types/progress';
import type { PricingRange } from '@/hooks/use-pricing-ranges';
import type { BarStripe } from '@/components/ui/stacked-progress-bar';
import { useViewerContext } from '@/hooks/use-viewer-context';
import type { ViewerContext } from '@/utils/member-display';
import { rangeMilestones, WEEKLY_STRIPES } from './progress-bars-helpers';
import { BarItem, useBarColors } from './progress-bar-item';

type DoubleBarsProps = {
  data: ProgressSummary;
  colors: Record<string, string>;
  milestones: BarStripe[];
  viewer: ViewerContext;
};

type BarSpec = {
  key: string;
  bucket: ProgressSummary['weekly'];
  label: string;
  heightClass: string;
  stripes: BarStripe[];
};

function buildSpecs(data: ProgressSummary, milestones: BarStripe[]): BarSpec[] {
  return [
    {
      key: 't',
      bucket: data.total,
      label: 'Total',
      heightClass: 'h-4',
      stripes: milestones,
    },
    {
      key: 'w',
      bucket: data.weekly,
      label: 'Objetivo semanal',
      heightClass: 'h-3',
      stripes: WEEKLY_STRIPES,
    },
  ];
}

function DoubleBars({
  data,
  colors,
  milestones,
  viewer,
}: DoubleBarsProps): ReactNode {
  const specs = buildSpecs(data, milestones);
  return (
    <div className="space-y-lg">
      {specs.map((s) => (
        <BarItem
          key={s.key}
          bucket={s.bucket}
          label={s.label}
          heightClass={s.heightClass}
          stripes={s.stripes}
          colors={colors}
          viewer={viewer}
        />
      ))}
    </div>
  );
}

export function ProgressBarsLoaded({
  data,
  ranges,
}: {
  data: ProgressSummary;
  ranges: PricingRange[];
}): ReactNode {
  const colors = useBarColors(data);
  const viewer = useViewerContext();
  const milestones = useMemo(
    () => rangeMilestones(ranges, data.total.goal),
    [ranges, data.total.goal]
  );
  return (
    <DoubleBars
      data={data}
      colors={colors}
      milestones={milestones}
      viewer={viewer}
    />
  );
}
